import { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/newRedesign/primitives/Button'
import { ActionIconButton } from '@/components/newRedesign/components/ActionIconButton'
import { FormSearchableSelect } from '@/components/newRedesign/form/FormSearchableSelect'
import { FormCurrencyInput } from '@/components/newRedesign/form/FormCurrencyInput'
import { FormSelect } from '@/components/newRedesign/form/FormSelect'
import { DataTable } from '@/components/newRedesign/components/DataTable'

import { ContainerCost } from '../CreateContainerWizard'
import {
  useCurrencies,
  useFixedCostCategoriesFetcher,
} from '../../hooks/useContainerQueries'

interface CostsStepProps {
  costs: ContainerCost[]
  onCostsChange: (_costs: ContainerCost[]) => void
  onCostModeChange?: (_isAddingCost: boolean) => void
  onFormRefChange?: (
    _ref: { submit: () => void; cancel: () => void } | null
  ) => void
}

interface CostFormData {
  fixedCostCategoryId: number | undefined
  cost: {
    amount: number
    codeCurrency: string
  }
  type: string
}

const costTypeLabels: Record<string, string> = {
  SUPPLIER: 'Proveedor',
  OPERATIONAL: 'Operacional',
  INDIRECT: 'Indirecto',
  UNDEFINED: 'Indefinido',
}

export function CostsStep({
  costs,
  onCostsChange,
  onCostModeChange,
  onFormRefChange,
}: CostsStepProps) {
  const [showCostForm, setShowCostForm] = useState(false)
  const [editingCostId, setEditingCostId] = useState<string | null>(null)

  const { control, handleSubmit, reset } = useForm<CostFormData>({
    defaultValues: {
      fixedCostCategoryId: undefined,
      cost: {
        amount: 0,
        codeCurrency: '',
      },
      type: '',
    },
  })

  // Usar hooks con cache de React Query
  const { data: currencies } = useCurrencies()
  const fetchCostCategories = useFixedCostCategoriesFetcher()

  const handleStartAddingCost = useCallback(() => {
    setEditingCostId(null)
    reset({
      fixedCostCategoryId: undefined,
      cost: {
        amount: 0,
        codeCurrency: '',
      },
      type: '',
    })
    setShowCostForm(true)
    onCostModeChange?.(true)
  }, [onCostModeChange, reset])

  const handleStartEditingCost = useCallback(
    (cost: ContainerCost) => {
      setEditingCostId(cost.id || null)
      reset({
        fixedCostCategoryId: cost.fixedCostCategoryId ?? undefined,
        cost: {
          amount: cost.registeredPrice?.amount ?? 0,
          codeCurrency: cost.registeredPrice?.codeCurrency ?? '',
        },
        type: cost.type,
      })
      setShowCostForm(true)
      onCostModeChange?.(true)
    },
    [onCostModeChange, reset]
  )

  const handleCancelCost = useCallback(() => {
    setShowCostForm(false)
    setEditingCostId(null)
    onCostModeChange?.(false)
    reset()
  }, [onCostModeChange, reset])

  const handleAddCost = useCallback(
    (data: CostFormData) => {
      // Validate that category is selected
      if (
        data.fixedCostCategoryId === undefined ||
        data.fixedCostCategoryId === null ||
        data.fixedCostCategoryId === 0
      ) {
        return
      }

      if (editingCostId) {
        // Update existing cost
        const updatedCosts = costs.map((cost) =>
          cost.id === editingCostId
            ? {
                ...cost,
                fixedCostCategoryId: data.fixedCostCategoryId ?? null,
                registeredPrice: {
                  amount: data.cost.amount,
                  codeCurrency: data.cost.codeCurrency,
                },
                type: data.type,
              }
            : cost
        )
        onCostsChange(updatedCosts)
      } else {
        // Add new cost
        const newCost: ContainerCost = {
          id: Date.now().toString(),
          fixedCostCategoryId: data.fixedCostCategoryId ?? null,
          registeredPrice: {
            amount: data.cost.amount,
            codeCurrency: data.cost.codeCurrency,
          },
          type: data.type,
        }
        onCostsChange([...costs, newCost])
      }

      setShowCostForm(false)
      setEditingCostId(null)
      onCostModeChange?.(false)
      reset()
    },
    [costs, editingCostId, onCostsChange, onCostModeChange, reset]
  )

  // Expose submit and cancel functions to parent
  useEffect(() => {
    if (showCostForm) {
      onFormRefChange?.({
        submit: () => handleSubmit(handleAddCost)(),
        cancel: handleCancelCost,
      })
    } else {
      onFormRefChange?.(null)
    }
  }, [
    showCostForm,
    onFormRefChange,
    handleSubmit,
    handleAddCost,
    handleCancelCost,
  ])

  const handleDeleteCost = (id: string) => {
    onCostsChange(costs.filter((cost) => cost.id !== id))
  }

  // Cost type options
  const costTypeOptions = [
    { id: 'SUPPLIER', title: 'Proveedor' },
    { id: 'OPERATIONAL', title: 'Operacional' },
    { id: 'INDIRECT', title: 'Indirecto' },
    { id: 'UNDEFINED', title: 'Indefinido' },
  ]

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      {!showCostForm ? (
        <>
          {/* Add Cost Button */}
          <div className="flex shrink-0 justify-start">
            <Button
              className="h-9 gap-2 bg-slate-800 hover:bg-slate-700"
              onClick={handleStartAddingCost}
              size="md"
              type="button"
            >
              <Plus className="h-4 w-4" />
              Costo
            </Button>
          </div>

          {/* Costs Table */}
          <div className="min-h-0 flex-1 overflow-auto rounded-md border border-[#E5E5E5]">
            <DataTable
              alignColumns={true}
              columns={['Tipo', 'Monto', 'Moneda', 'Acciones']}
              data={costs}
              emptyMessage="No hay costos agregados"
              onRowClick={(cost) => handleStartEditingCost(cost)}
              renderRow={(cost) => {
                return (
                  <>
                    <td className="px-3 py-1.5 text-left text-sm">
                      {costTypeLabels[cost.type] || cost.type}
                    </td>
                    <td className="px-3 py-1.5 text-center text-sm font-medium">
                      {(() => {
                        const amount = cost.registeredPrice?.amount
                        if (amount === undefined || amount === null) {
                          return '0.00'
                        }

                        if (Number.isInteger(amount)) {
                          return amount.toFixed(2)
                        }

                        const amountStr = amount.toString()
                        const decimalPart = amountStr.split('.')[1]
                        if (decimalPart && decimalPart.length > 10) {
                          return Number(amount.toFixed(10)).toString()
                        }

                        return amountStr
                      })()}
                    </td>
                    <td className="px-3 py-1.5 text-center text-sm">
                      {cost.registeredPrice?.codeCurrency || '-'}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <ActionIconButton
                          className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700"
                          icon={<Trash2 className="h-4 w-4" />}
                          label="Eliminar"
                          onClick={(e) => {
                            e.stopPropagation()
                            if (cost.id) {
                              handleDeleteCost(cost.id)
                            }
                          }}
                          size="icon"
                          variant="ghost"
                        />
                      </div>
                    </td>
                  </>
                )
              }}
            />
          </div>
        </>
      ) : (
        <>
          {/* Cost Form - Matching CostDialog design */}
          <div className="flex flex-col gap-4">
            {/* Title */}
            <h3 className="text-base font-semibold leading-6 text-[#262626]">
              {editingCostId ? 'Editar costo' : 'Nuevo costo'}
            </h3>

            {/* Category Selection */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Categoría <span className="text-[#262626]">(*)</span>
              </label>
              <FormSearchableSelect
                control={control}
                emptyMessage="No se encontraron categorías de costo"
                fetcher={fetchCostCategories}
                name="fixedCostCategoryId"
                placeholder="Seleccione una categoría"
                rules={{ required: 'La categoría es requerida' }}
                searchPlaceholder="Buscar categoría..."
                showLabel={false}
              />
            </div>

            {/* Cost and Currency */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Costo <span className="text-[#262626]">(*)</span>
              </label>
              {currencies && (
                <FormCurrencyInput
                  control={control}
                  currenciesData={currencies.map(({ code }) => ({
                    code,
                    displayName: code,
                  }))}
                  name="cost"
                  placeholder="0.00"
                  rules={{
                    required: 'El costo es requerido',
                    validate: (value: {
                      amount?: number
                      codeCurrency: string
                    }) => {
                      const errors: string[] = []

                      if (
                        value?.amount === undefined ||
                        value?.amount === null ||
                        value.amount <= 0
                      ) {
                        errors.push('El monto debe ser mayor a 0')
                      }

                      if (
                        !value?.codeCurrency ||
                        value.codeCurrency.trim() === ''
                      ) {
                        errors.push('La moneda es requerida')
                      }

                      if (value?.amount !== undefined && value.amount > 0) {
                        const amountStr = value.amount.toString()
                        const decimalPart = amountStr.split('.')[1]
                        if (decimalPart && decimalPart.length > 10) {
                          errors.push(
                            'El monto no puede tener más de 10 decimales'
                          )
                        }
                      }

                      return errors.length === 0 || errors.join('. ')
                    },
                  }}
                  showLabel={false}
                />
              )}
            </div>

            {/* Cost Type */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]">
                Tipo <span className="text-[#262626]">(*)</span>
              </label>
              <FormSelect
                control={control}
                data={costTypeOptions.map((type) => ({
                  id: type.id,
                  title: type.title,
                }))}
                name="type"
                placeholder="Seleccione un tipo"
                rules={{ required: 'El tipo de costo es requerido' }}
                showLabel={false}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
